// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { TopSummaryBoxConfig } from 'sonata-react-components';
import { normalize, normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { isToday } from '@/helpers/reporting.helpers';
import { getBaseCurrency } from '@/store/selectors/baseCurrency.selectors';
import { getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getCampaigns, getCurrency, getTimeFrame } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { useSummaryContext } from '@/ts-contexts/reporting/Summary/SummaryContext';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';

type Props = {
  filterSection: string;
  showAnalyticalView?: boolean;
  showCampaignsBreakdowns?: boolean;
};

type State = 'idle' | 'success' | 'empty';

export function useCampaignSummary({
  filterSection,
  showAnalyticalView = false,
  showCampaignsBreakdowns = false
}: Props) {
  const { formatMessage } = useIntl();
  const [generatingItemsStatus, setGeneratingItemsStatus] = useState<State>('idle');

  // @ts-ignore
  const currency = useSelector(store => getCurrency(store, filterSection));
  // @ts-ignore
  const currencyInfo = useSelector(store => getBaseCurrency(store, currency)) as BaseCurrencyInterface;
  const campaignIds: string[] = useSelector(getCampaigns);
  const reportingCampaigns = useSelector(getSelectedCampaigns);
  const campaigns: Record<string, any>[] = reportingCampaigns.filter(({ id }: { id: string }) =>
    campaignIds.includes(id)
  );
  const timeFrame: {
    beginDate: string;
    endDate: string;
    // @ts-ignore
  } = useSelector(store => getTimeFrame(store, filterSection));

  const {
    getSummaryDOOHStats,
    getSummaryItem,
    getSummaryStats,
    getSummaryStatsByCampaign,
    summaryItemsKeys,
    summaryStatsReady
  } = useSummaryContext();
  const stats = {
    ...getSummaryStats(),
    ...getSummaryDOOHStats()
  };
  const statsByCampaign = getSummaryStatsByCampaign!();

  const summary = useMemo(() => {
    if (summaryStatsReady) {
      const items = summaryItemsKeys.map(itemKey => {
        const summaryItem = getSummaryItem?.(itemKey, campaigns, stats);

        const getItemClass = () => {
          if (summaryItem && !summaryItem.graph) {
            const { main, secondary } = summaryItem;
            const secondaryElements = secondary ? secondary.length : 0;
            let className = '';
            if (main) {
              className += secondaryElements > 0 ? 'mainData' : 'mainDataOnly';
            }
            if (secondaryElements >= 3) {
              className += ' tripleSecondaryData';
            } else if (secondaryElements > 1) {
              className += ' secondaryDataList';
            }
            if (secondaryElements === 1) {
              className += ' secondaryData';
            }
            return className;
          }
          if (summaryItem) {
            return 'graphData';
          }
          return '';
        };

        const getTopBoxChartSeriesConfig = (name: string, data: Record<string, any>[]) => {
          if (showAnalyticalView) {
            return {
              areaConfig: {
                fillColor: 'rgba(188,202,247,0.8)',
                fillOpacity: 1
              },
              height: 100,
              legend: false,
              lineOptions: {
                lineColor: '#4F5D88',
                lineWidth: 1
              },
              plotMarkersEnabled: false,
              series: [
                {
                  data,
                  name
                }
              ],
              tooltipHoverEnabled: false,
              type: 'area',
              xAxisConfig: {
                visible: false
              },
              yAxisConfigs: [
                {
                  visible: false
                }
              ]
            };
          }
        };

        const getTopBoxTrendState = (data: Record<string, any>[], hoursData: number[]): 'up' | 'down' | undefined => {
          if (data && showAnalyticalView && isToday(timeFrame.endDate)) {
            const analyticalViewData = [...data];
            const last24hours = hoursData.reduce((a, b) => a + b, 0);
            // @ts-ignore
            const average = analyticalViewData.reduce((a, b) => a + b, 0) / analyticalViewData.length;
            if (last24hours >= average) {
              return 'up';
            }
            if (last24hours < average) {
              return 'down';
            }
          }
        };

        const formatValue = (value: string, isCurrency: boolean, isPercentage: boolean, fixed = 0) => {
          if (isCurrency) {
            return normalizeWithUnit(value, currencyInfo, fixed);
          }
          return `${normalize(value, fixed, currencyInfo.countries?.[0])}${isPercentage ? '%' : ''}`;
        };

        const hasSameGoalType = () => {
          return (
            new Set(campaigns ? campaigns.map(campaign => campaign.overallGoal && campaign.overallGoal.type) : [])
              .size <= 1
          );
        };

        const className = getItemClass();

        const getTopBoxConfig = (overviewItem: Record<string, any>): TopSummaryBoxConfig => {
          return {
            chartSeries: getTopBoxChartSeriesConfig(overviewItem.main.name, overviewItem.main.analyticalView),
            displayGoalMax: formatValue(
              overviewItem.goalMax,
              overviewItem.main.isCurrency,
              overviewItem.main.isPercentage,
              overviewItem.main.decimalDigits
            ),
            displayNumber: formatValue(
              overviewItem.main.value,
              overviewItem.main.isCurrency,
              overviewItem.main.isPercentage,
              overviewItem.main.decimalDigits
            ),
            goalMax: hasSameGoalType() && overviewItem.goalMax,
            icon: overviewItem.main.icon || 'fal fa-smile',
            number: overviewItem.main.value,
            progressBarColor: overviewItem.progressBarColor || 'line',
            text: formatMessage({ id: overviewItem.main.name }),
            ...(overviewItem.main.subtitle && {
              subtitle: formatMessage(
                { id: overviewItem.main.subtitle },
                {
                  bold: chunks => <strong>{chunks}</strong>,
                  value: overviewItem.main.subtitleValue
                }
              )
            }),
            trendState: getTopBoxTrendState(overviewItem.main.analyticalView, overviewItem.main.analyticalViewHours)
          };
        };

        const getMiddleBoxConfig = (overviewItem: Record<string, any>) => {
          if (!overviewItem.secondary) {
            return [];
          }
          return overviewItem.secondary.map((itemSecondary: Record<string, any>) => ({
            icon: itemSecondary.icon,
            numberLabelConfig: {
              amount: itemSecondary.value || 0,
              country: currencyInfo.countries?.[0],
              decimals: itemSecondary.decimalDigits || 0,
              symbol: itemSecondary.isPercentage
                ? '%'
                : itemSecondary.isCurrency
                  ? currencyInfo.currencySymbol
                  : itemSecondary.unit,
              symbolPosition: itemSecondary.isPercentage || itemSecondary.unit ? 'right' : currencyInfo.currencyPosition
            },
            ...(itemSecondary.name2 && {
              numberLabelConfig2: {
                amount: itemSecondary.value2 || 0,
                country: currencyInfo.countries?.[0],
                decimals: itemSecondary.decimalDigits || 0,
                symbol: itemSecondary.isPercentage
                  ? '%'
                  : itemSecondary.isCurrency
                    ? currencyInfo.currencySymbol
                    : null,
                symbolPosition: itemSecondary.isPercentage ? 'right' : currencyInfo.currencyPosition
              }
            }),
            text: `${formatMessage({
              id: itemSecondary.name
            })}${itemSecondary.name2 ? ` / ${formatMessage({ id: itemSecondary.name2 })}` : ''}`
          }));
        };

        const getBottomConfig = (summaryItemData: Record<string, any>) => {
          const getTagLabels = (familyItemApplies: boolean, secondaries: Record<string, any>[] = []) => {
            if (!familyItemApplies) {
              return [];
            }
            return secondaries.map(secondaryItem => {
              const secondaryName = formatMessage({ id: secondaryItem.name });
              const secondaryFormattedValue = formatValue(
                secondaryItem.value,
                secondaryItem.isCurrency,
                secondaryItem.isPercentage,
                secondaryItem.decimalDigits
              );
              return `${secondaryName}: ${secondaryFormattedValue}`;
            });
          };

          const getSummaryConfigBoxes = () => {
            const summaryItems = [summaryItemData];
            if (itemKey !== 'spend') {
              summaryItems.push(getSummaryItem('spend', campaigns, stats));
            }
            return summaryItems.map(summaryItemEntry => ({
              middle: getMiddleBoxConfig(summaryItemEntry),
              top: {
                ...getTopBoxConfig(summaryItemEntry),
                tiny: true
              }
            }));
          };

          if (campaigns.length > 1) {
            const campaignsBreakdowns: Record<string, any>[] = campaigns.map(campaignData => {
              const cid = campaignData.id;
              const campaignStats = {
                ...(statsByCampaign[cid] || {}),
                screenAvgImpacts: stats.screenAvgImpactsByCampaign ? stats.screenAvgImpactsByCampaign[cid] : 0,
                screensNumber: stats.screensNumberByCampaign ? stats.screensNumberByCampaign[cid] : 0
              };
              return {
                ...(campaignData as Record<string, any>),
                ...getSummaryItem(itemKey, [campaignData], campaignStats)
              };
            });
            return {
              items: campaignsBreakdowns.map(({ familyItemApplies, id, main, name, secondary }) => ({
                currencyInfo: {
                  countries: currencyInfo.countries,
                  currencyPosition: summaryItemData.main.isPercentage ? 'right' : currencyInfo.currencyPosition,
                  currencySymbol: summaryItemData.main.isPercentage
                    ? '%'
                    : summaryItemData.main.isCurrency
                      ? currencyInfo.currencySymbol
                      : null
                },
                id,
                name,
                progressBarData: {
                  amount: main.value || 0,
                  country: currencyInfo.countries?.[0],
                  decimals: summaryItemData.main.decimalDigits || 0,
                  target: summaryItemData.main.value
                },
                showNA: !familyItemApplies,
                tagLabels: getTagLabels(!!familyItemApplies, secondary)
              })),
              minimumItemsWarning: formatMessage({
                defaultMessage: 'You need to mark a minimum of three campaign lines to save the changes.',
                id: 'reporting.summary.campaignLinesModal.warningMessage'
              }),
              modalFilterPlaceHolder: formatMessage({
                defaultMessage: 'Search by name',
                id: 'reporting.summary.campaignLinesModal.placeHolderInputSearch'
              }),
              modalShowFilter: true,
              modalShowSortBy: true,
              modalSubTitle: formatMessage({
                defaultMessage: 'Choose the 3 campaign lines you want to mark in your main box.',
                id: 'reporting.summary.campaignLinesModal.subTitle'
              }),
              modalSummaryBoxesConfig: getSummaryConfigBoxes(),
              modalTitle: formatMessage({
                defaultMessage: 'All Campaign Lines',
                id: 'reporting.summary.campaignLinesModal.title'
              }),
              show: showCampaignsBreakdowns
            };
          }
          return {
            items: [],
            modalTitle: '',
            show: false
          };
        };

        return {
          bottom: {
            ...getBottomConfig(summaryItem!),
            showAll: campaigns.length > 3
          },
          footerConfig: summaryItem?.footerConfig,
          itemClassName: className,
          key: itemKey,
          middle: getMiddleBoxConfig(summaryItem!),
          summaryItem,
          top: getTopBoxConfig(summaryItem!)
        };
      });
      setGeneratingItemsStatus('success');
      return items;
    }
  }, [showAnalyticalView, showCampaignsBreakdowns, summaryItemsKeys, summaryStatsReady]);

  return {
    isReady: summaryStatsReady && generatingItemsStatus === 'success',
    items: summaryStatsReady ? summary : []
  };
}
